import { useRef } from 'react';

import useNotify from '@hooks/notify';

import CustomTableHeader from './CustomTableHeader';
import CustomTablePagination from './CustomtablePagination';
import CustomTableRow from './CustomTableRow';
import { CustomTableHeaderProps, CustomTableRowData } from './types';

interface CustomTableProps<T extends string | number = string> {
  headers: CustomTableHeaderProps[];
  data: CustomTableRowData<T>[];
  handleSelect?: (row: CustomTableRowData<T>) => void;
  totalCount?: number;
  onPageChange?: (count: number) => void;
  currentPage?: number;
  hasMultiSelect?: boolean;
  handleSelectAll?: (selectedIds: T[], removedIds: T[]) => void;
  selectedRows?: T[];
  handleCheckboxSelect?: (row: CustomTableRowData<T>) => void;
  maxLimit?: number;
  disabledRowIds?: T[];
  hasInfiniteScroll?: boolean;
  className?: string;
  customRowClassName?: string;
}

const CustomTable = <T extends string | number = string>({
  headers,
  data,
  handleSelect,
  totalCount,
  onPageChange,
  currentPage,
  hasMultiSelect,
  handleSelectAll,
  selectedRows,
  handleCheckboxSelect,
  maxLimit,
  disabledRowIds,
  hasInfiniteScroll = false,
  className = '',
  customRowClassName = ''
}: CustomTableProps<T>) => {
  const { createErrorAlert } = useNotify();

  const infiniteScrollRef = useRef<HTMLDivElement>(null);

  const showPagination = totalCount && totalCount > 0;

  const isSelectionLimitReached = (itemCount = 1) =>
    maxLimit && selectedRows && selectedRows.length + itemCount > maxLimit;

  const handleSelectAllCheckbox = (checked: boolean) => {
    const filteredData = checked
      ? data
          .filter((item) => !selectedRows?.includes(item.id))
          .filter((item) => !disabledRowIds?.includes(item.id))
      : data
          .filter((item) => selectedRows?.includes(item.id))
          .filter((item) => !disabledRowIds?.includes(item.id));

    if (checked) {
      if (isSelectionLimitReached(filteredData.length)) {
        createErrorAlert('Limit reached');
        return;
      }
      handleSelectAll?.(
        filteredData.map((item) => item.id),
        []
      );
    } else {
      handleSelectAll?.(
        [],
        filteredData.map((item) => item.id)
      );
    }
  };

  const handleCheckboxSelectCustom = (row: CustomTableRowData<T>) => {
    if (disabledRowIds?.includes(row.id)) return;
    if (isSelectionLimitReached() && !selectedRows?.includes(row.id))
      createErrorAlert('Limit reached');
    else handleCheckboxSelect?.(row);
  };

  const handleSelectCustom = (row: CustomTableRowData<T>) => {
    if (!handleSelect) return;
    if (disabledRowIds?.includes(row.id)) return;
    if (isSelectionLimitReached() && !selectedRows?.includes(row.id))
      createErrorAlert('Limit reached');
    else handleSelect(row);
  };

  const isAllRowSelected = () =>
    data
      .filter((item) => !disabledRowIds?.includes(item.id))
      .every((item) => selectedRows?.includes(item.id));

  const isAllRowDisabled = () =>
    data.every((item) => disabledRowIds?.includes(item.id));

  return (
    <div className={`${showPagination ? 'pb-4' : 'pb-18'} ${className}`}>
      <CustomTableHeader
        headers={headers}
        hasMultiSelect={hasMultiSelect}
        handleSelectAll={handleSelectAllCheckbox}
        isAllRowSelected={isAllRowSelected()}
        isAllRowDisabled={isAllRowDisabled()}
      />
      <CustomTableRow<T>
        headers={headers}
        data={data}
        handleSelect={handleSelectCustom}
        hasRowClickHandler={Boolean(handleSelect)}
        hasMultiSelect={hasMultiSelect}
        selectedRows={selectedRows}
        disabledRowIds={disabledRowIds}
        handleCheckboxSelect={handleCheckboxSelectCustom}
        customClassName={customRowClassName}
      />
      {showPagination &&
        (hasInfiniteScroll ? (
          <div ref={infiniteScrollRef} className="h-1" />
        ) : (
          <CustomTablePagination
            currentPage={currentPage || 1}
            totalCount={totalCount || 0}
            onPageChange={onPageChange || (() => {})}
            isFixed={false}
          />
        ))}
    </div>
  );
};

export default CustomTable;
